/**
 * "Merlin found answers" (kanban 3eed0be5): the candidates the daily sweep
 * thinks are already answered, each with the answer, the evidence and two
 * one-click choices — Confirm (close the item with that answer) or Still
 * open — plus a quiet fold of what the sweep closed on its own this week.
 * Nothing here writes to anyone: Confirm records the answer on the item.
 */
import { useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import { trackEvent } from '../../services/portalTracking';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import {
  confirmFoundAnswer,
  declineFoundAnswer,
  type FoundAnswerRead,
} from '../../services/qcpFoundAnswers';
import {
  askSendState,
  evidenceLine,
  kindWord,
  outcomeWords,
  placeWord,
  questionTitle,
  retiredByWord,
  sendStateWords,
  splitFoundAnswers,
  verdictWords,
  type FoundAnswerOutcome,
} from './foundAnswersView';
import styles from './FoundAnswers.module.css';
import { useQualityReadOnly } from '../../context/QualityReadOnlyContext';

export interface FoundAnswersProps {
  rows: FoundAnswerRead[];
  /** Called with the updated row after Confirm / Still open went through. */
  onChanged: (updated: FoundAnswerRead) => void;
  /** How many to show before "Show N more" (UX round 2026-10-27: 17 of
   *  them filled the first screen of Triage, above the one item that
   *  needed a decision). */
  foldAt?: number;
  /** The door's asks: each ask card says whether it was ever sent. */
  asks?: DoorDataRequestRead[];
}

interface Decided {
  row: FoundAnswerRead;
  outcome: FoundAnswerOutcome;
  /** The card's height when pressed: the receipt keeps it, so nothing
   *  below moves under the pointer. */
  height: number;
}

export function FoundAnswers({ rows, onChanged, foldAt = 3, asks = [] }: FoundAnswersProps) {
  // A read-only viewer sees what Merlin found; the desk rules on it (kanban 808dabf6).
  const readOnly = useQualityReadOnly();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  // Pressed this visit: the card stays where it was as a one-line receipt.
  const [decided, setDecided] = useState<Record<string, Decided>>({});
  // The order cards were first listed in, so a receipt keeps its place.
  const order = useRef<string[]>([]);
  const { candidates, closed: allClosed } = splitFoundAnswers(rows);
  for (const c of candidates) if (!order.current.includes(c.id)) order.current.push(c.id);
  const live = new Map(candidates.filter((c) => !decided[c.id]).map((c) => [c.id, c]));
  const listed = order.current.filter((id) => live.has(id) || decided[id]);
  const waiting = live.size;
  // A question confirmed here shows as its receipt, not again under "closed this week".
  const closed = allClosed.filter((r) => !decided[r.id]);
  if (listed.length === 0 && closed.length === 0) return null;
  const folded = listed.length - foldAt;
  const shown = showAll || folded <= 0 ? listed : listed.slice(0, foldAt);

  const act = async (
    event: MouseEvent<HTMLButtonElement>,
    row: FoundAnswerRead,
    label: 'qcp_found_answer_confirm' | 'qcp_found_answer_decline',
    work: () => Promise<FoundAnswerRead>,
  ) => {
    if (busyId) return;
    const height = event.currentTarget.closest('li')?.getBoundingClientRect().height ?? 0;
    setBusyId(row.id);
    setError(null);
    try {
      const updated = await work();
      trackEvent('action', label, {
        answer_sweep_item_id: row.id,
        item_kind: row.item_kind,
        item_id: row.item_id,
        thread_no: row.thread_no || null,
        investigation_no: row.investigation_no || null,
        result: 'request_sent',
      });
      setDecided((prev) => ({
        ...prev,
        [row.id]: { row, outcome: label === 'qcp_found_answer_confirm' ? 'confirmed' : 'kept_open', height },
      }));
      onChanged(updated);
    } catch {
      setError('That did not go through. Try again in a moment.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className={styles.box} aria-labelledby="found-answers" data-testid="found-answers">
      <h2 id="found-answers" className={styles.head}>
        Merlin found answers
        {waiting > 0 && (
          <span className={styles.count} data-testid="found-answers-count">
            {waiting} to confirm
          </span>
        )}
      </h2>
      {listed.length > 0 && (
        <ul className={styles.list}>
          {shown.map((id) => {
            const done = decided[id];
            if (done) {
              const place = placeWord(done.row);
              return (
                <li
                  key={id}
                  className={`${styles.row} ${styles.receipt}`}
                  data-testid="found-answer-receipt"
                  data-outcome={done.outcome}
                  role="status"
                  style={done.height ? { minHeight: done.height } : undefined}
                >
                  <div className={styles.line}>
                    <span aria-hidden="true">{done.outcome === 'confirmed' ? '✓ ' : '↺ '}</span>
                    <strong>{outcomeWords(done.outcome)}</strong>: <em>“{questionTitle(done.row)}”</em>
                    {done.row.person_name && <> to {done.row.person_name}</>}
                    {place && <> on {place}</>}
                  </div>
                </li>
              );
            }
            const row = live.get(id) as FoundAnswerRead;
            const busy = busyId === row.id;
            const place = placeWord(row);
            const sendState = askSendState(row, asks);
            return (
              <li key={row.id} className={styles.row} data-testid="found-answer-row">
                <div className={styles.text}>
                  <div className={styles.line}>
                    The {kindWord(row)} <em>“{questionTitle(row)}”</em>
                    {row.person_name && <> to <strong>{row.person_name}</strong></>}
                    {place && (
                      <>
                        {' '}on <Link className={styles.place} to={row.link.replace(/^\/portal\/quality-copilot\/?/, '')}>{place}</Link>
                      </>
                    )}
                    {sendState && (
                      <span className={styles.sendState} data-testid="found-answer-sent" data-kind={sendState.kind}>
                        {' '}({sendStateWords(sendState)})
                      </span>
                    )}{' '}
                    {verdictWords(row, sendState)}:
                  </div>
                  {row.answer_summary && <p className={styles.answer}>{row.answer_summary}</p>}
                  {row.evidence.length > 0 && (
                    <ul className={styles.evidence} aria-label="Evidence">
                      {row.evidence.slice(0, 4).map((e) => (
                        <li key={e.id}>{evidenceLine(e)}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {!readOnly && (<div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.confirm}
                    disabled={busy}
                    data-action="qcp_found_answer_confirm"
                    data-target-found-answer-id={row.id}
                    onClick={(e) => void act(e, row, 'qcp_found_answer_confirm', () => confirmFoundAnswer(row.id))}
                  >
                    Yes, that answers it
                  </button>
                  <button
                    type="button"
                    className={styles.decline}
                    disabled={busy}
                    data-action="qcp_found_answer_decline"
                    data-target-found-answer-id={row.id}
                    onClick={(e) => void act(e, row, 'qcp_found_answer_decline', () => declineFoundAnswer(row.id))}
                  >
                    Still open
                  </button>
                </div>)}
              </li>
            );
          })}
        </ul>
      )}
      {folded > 0 && (
        <button
          type="button"
          className={styles.fold}
          aria-expanded={showAll}
          data-testid="found-answers-more"
          onClick={() => {
            trackEvent('action', 'qcp_found_answers_more_toggle', {
              open: !showAll,
              hidden: folded,
              result: 'panel_toggled',
            });
            setShowAll((v) => !v);
          }}
        >
          {showAll ? 'Show fewer' : `Show ${folded} more to confirm`}
        </button>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {closed.length > 0 && (
        <div className={styles.closedWrap}>
          <button
            type="button"
            className={styles.fold}
            aria-expanded={showClosed}
            onClick={() => {
              trackEvent('action', 'qcp_found_answers_closed_toggle', { open: !showClosed, result: 'panel_toggled' });
              setShowClosed((v) => !v);
            }}
          >
            {showClosed ? 'Hide' : 'Show'} {closed.length} closed this week
          </button>
          {showClosed && (
            <ul className={styles.closed} data-testid="found-answers-closed">
              {closed.map((row) => (
                <li key={row.id}>
                  <em>“{questionTitle(row)}”</em>
                  {placeWord(row) && <> on {placeWord(row)}</>} — closed by {retiredByWord(row)}
                  {row.answer_summary && <>: {row.answer_summary}</>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

export default FoundAnswers;
